import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import {
  WorkOrdersByPriority,
  WorkOrderPriorityOrder,
} from "../models/workOrderTypes";
import localStorageHelper from "../../../app/localStorage";
import WorkOrderQueueAPI from "../api/workOrderApi";
import { convertToRGBA } from "../../common/Utils/commonUtilityFunctions";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import packageJson from "../../../../package.json";

const PRIORITY_CONFIG_DROPPABLE_ID = "priority-config-columns";

const normalizePriorityColumns = (
  columns:
    | WorkOrdersByPriority[]
    | Record<string, WorkOrdersByPriority>
    | null
    | undefined,
): WorkOrdersByPriority[] => {
  const columnsAsArray = Array.isArray(columns)
    ? columns.slice()
    : Object.values(columns ?? {});

  return [...columnsAsArray].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );
};

interface PriorityBoardCustomColumnsProps {
  columns:
    | WorkOrdersByPriority[]
    | Record<string, WorkOrdersByPriority>
    | null
    | undefined;
  onSave: (updatedColumns: WorkOrdersByPriority[]) => void;
  onCancel: () => void;
}

const PriorityBoardCustomColumns: React.FC<PriorityBoardCustomColumnsProps> = ({
  columns,
  onSave,
  onCancel,
}) => {
  const user = localStorageHelper.getByKey("user");
  const [orderedColumns, setOrderedColumns] = React.useState<
    WorkOrdersByPriority[]
  >(() => normalizePriorityColumns(columns));

  React.useEffect(() => {
    setOrderedColumns(normalizePriorityColumns(columns));
  }, [columns]);

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedColumns = items.map((column, index) => ({
      ...column,
      displayOrder: index + 1,
    }));

    setOrderedColumns(updatedColumns);

    const payload: WorkOrderPriorityOrder[] = updatedColumns.map((column) => ({
      priorityIdentity: column.identity,
      displayOrder: column.displayOrder,
      modifiedByUserIdentity: user.identity,
      modifiedDate: new Date().toISOString(),
    }));

    try {
      await WorkOrderQueueAPI.saveWorkOrderPriorityOrder(payload);
    } catch {
      setOrderedColumns(normalizePriorityColumns(columns));
      return;
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      { company: user.company, user: user.userName },
      "onDragEndPriorityColumnOrder",
      {
        elapsed_ms: "124",
        title: "Priority Board Column Customization",
        componentName: "PriorityBoardCustomColumns.tsx",
        version: packageJson.version,
      },
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: "100%", margin: "0 auto" }}>
      <Typography variant="h5" gutterBottom>
        Configure Column Order
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Drag and drop columns to reorder them
      </Typography>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={PRIORITY_CONFIG_DROPPABLE_ID}
          direction="horizontal"
        >
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                position: "relative",
                my: 2,
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 2,
                overflowX: "auto",
                overflowY: "visible",
                minHeight: 280,
                py: 1,
              }}
            >
              {orderedColumns.map((column, index) => (
                <Draggable
                  key={column.identity}
                  draggableId={`priority-col-${column.identity}`}
                  index={index}
                >
                  {(dragProvided) => (
                    <Paper
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      elevation={2}
                      sx={{
                        p: 2,
                        minHeight: 250,
                        minWidth: 250,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        bgcolor: "lightgray",
                        cursor: "grab",
                        "&:active": {
                          cursor: "grabbing",
                        },
                      }}
                    >
                      <Typography
                        display="flex"
                        sx={{
                          fontStyle: "normal",
                          fontWeight: "500",
                          fontSize: "15px",
                          color: "#121217",
                          mb: 1,
                        }}
                      >
                        <Chip
                          label=""
                          size="small"
                          sx={{
                            bgcolor: convertToRGBA(column.statusColor),
                            color: column.textColor,
                            mb: "3px",
                            mr: "5px",
                            width: "12px",
                            height: "12px",
                          }}
                        />
                        {column.displayText}
                      </Typography>
                      <Typography variant="caption">
                        Order: {column.displayOrder ?? index + 1}
                      </Typography>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button sx={{ textTransform: "capitalize" }} variant="contained" onClick={() => onSave(orderedColumns)}>
          Save
        </Button>
        <Button sx={{ textTransform: "capitalize" }} variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default PriorityBoardCustomColumns;
